import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { translations } from "../lib/i18n";
import { api } from "../lib/api";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
    const [lang, setLang] = useState(() => localStorage.getItem("sraa_lang") || "ar");
    const [cart, setCart] = useState(() => {
        try {
            const raw = localStorage.getItem("sraa_cart");
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });
    const [cartOpen, setCartOpen] = useState(false);

    useEffect(() => {
        const dir = lang === "ar" ? "rtl" : "ltr";
        document.documentElement.dir = dir;
        document.documentElement.lang = lang;
        localStorage.setItem("sraa_lang", lang);
    }, [lang]);

    useEffect(() => {
        localStorage.setItem("sraa_cart", JSON.stringify(cart));
    }, [cart]);

    // Reconcile cart with current catalog: refresh stale product_ids/prices by name match.
    // Prevents "Product not found" errors after admin reseeds and UUIDs change.
    useEffect(() => {
        if (cart.length === 0) return;
        let cancelled = false;
        api.get("/products").then((r) => {
            if (cancelled) return;
            const products = r.data || [];
            const byId = new Map(products.map((p) => [p.id, p]));
            const byName = new Map();
            products.forEach((p) => {
                byName.set(p.name_ar, p);
                byName.set(p.name_en, p);
            });
            const reconciled = cart
                .map((it) => {
                    const live = byId.get(it.product_id) || byName.get(it.product_name);
                    if (!live) return null;
                    let livePrice = it.price;
                    if (it.variant_label) {
                        const v = (live.variants || []).find((x) => x.label === it.variant_label);
                        if (v) livePrice = v.price;
                    } else if (live.base_price) {
                        livePrice = live.base_price;
                    }
                    return { ...it, product_id: live.id, price: livePrice };
                })
                .filter(Boolean);
            const changed = JSON.stringify(reconciled) !== JSON.stringify(cart);
            if (changed) setCart(reconciled);
        }).catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const t = useMemo(() => translations[lang], [lang]);

    const addToCart = (item) => {
        setCart((prev) => {
            const key = `${item.product_id}:${item.variant_label || ""}`;
            const existing = prev.find(
                (p) => `${p.product_id}:${p.variant_label || ""}` === key
            );
            if (existing) {
                return prev.map((p) =>
                    `${p.product_id}:${p.variant_label || ""}` === key
                        ? { ...p, quantity: p.quantity + item.quantity }
                        : p
                );
            }
            return [...prev, item];
        });
        setCartOpen(true);
    };

    const removeFromCart = (productId, variantLabel) => {
        setCart((prev) =>
            prev.filter(
                (p) => !(p.product_id === productId && (p.variant_label || "") === (variantLabel || ""))
            )
        );
    };

    const updateQty = (productId, variantLabel, qty) => {
        if (qty <= 0) return removeFromCart(productId, variantLabel);
        setCart((prev) =>
            prev.map((p) =>
                p.product_id === productId && (p.variant_label || "") === (variantLabel || "")
                    ? { ...p, quantity: qty }
                    : p
            )
        );
    };

    const clearCart = () => setCart([]);

    const cartTotal = cart.reduce((s, it) => s + it.price * it.quantity, 0);
    const cartCount = cart.reduce((s, it) => s + it.quantity, 0);

    const value = {
        lang, setLang, t,
        cart, addToCart, removeFromCart, updateQty, clearCart, cartTotal, cartCount,
        cartOpen, setCartOpen,
    };
    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used within AppProvider");
    return ctx;
};
